// SourceDialogs.jsx - Gestion des dialogues pour l'édition et l'affichage des résumés
import React, { useState, useEffect } from 'react';
import { Dialog, Typography } from '@mui/material';
import { Close, InfoOutlined, OpenInNew } from '@mui/icons-material';

export default function SourceDialogs({
  editDialogOpen = false,
  summaryDialogOpen = false,
  source = null,
  editedSource = {},
  onEditClose,
  onSummaryClose,
  onSaveEdit
}) {
  // État local pour le formulaire d'édition
  const [formData, setFormData] = useState(editedSource);
  
  // Synchroniser le formulaire avec les props
  useEffect(() => {
    setFormData(editedSource);
  }, [editedSource]);
  
  // Gestionnaire de changement de champ
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };
  
  // Gestionnaire de sauvegarde
  const handleSave = () => {
    // Validation minimale
    if (!formData.domain) return;
    
    onSaveEdit(formData);
  };
  
  return (
    <>
      {/* Dialogue d'édition de source */}
      <Dialog open={editDialogOpen} onClose={onEditClose} maxWidth="md" fullWidth>
        <div className="bg-white rounded-lg overflow-hidden shadow-xl max-w-4xl w-full">
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {source ? 'Modifier la source' : 'Ajouter une nouvelle source'}
            </h3>
            <button 
              onClick={onEditClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <Close className="h-5 w-5" />
            </button>
          </div>
          
          <div className="px-6 py-5 space-y-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="domain" className="block text-sm font-medium text-gray-700">Domaine</label>
                <input
                  type="text"
                  id="domain"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  value={formData.domain || ''}
                  onChange={handleInputChange}
                  required
                  placeholder="Ex: example.com"
                />
                <p className="mt-1 text-xs text-gray-500">Domaine du site, sans http:// ou www.</p>
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  id="description"
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  placeholder="Description courte du contenu de cette source"
                />
              </div>
              
              {/* Champ pour le résumé */}
              {formData.summary && (
                <div>
                  <label htmlFor="summary" className="block text-sm font-medium text-gray-700 flex items-center">
                    Résumé généré par IA
                    <span className="ml-2 text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                      IA
                    </span>
                  </label>
                  <textarea
                    id="summary"
                    rows={6}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm bg-purple-50"
                    value={formData.summary || ''}
                    onChange={handleInputChange}
                    placeholder="Résumé détaillé généré par l'IA"
                  />
                  <p className="mt-1 text-xs text-gray-500">Ce résumé a été généré automatiquement et peut être modifié si nécessaire.</p>
                </div>
              )}
              
              <div>
                <label htmlFor="link" className="block text-sm font-medium text-gray-700">Lien</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                    https://
                  </span>
                  <input
                    type="text"
                    id="link"
                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    value={(formData.link || '').replace(/^https?:\/\//, '')}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        link: `https://${value}`.replace(/^https:\/\/https:\/\//, 'https://')
                      }));
                    }}
                    placeholder="www.example.com/page"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">URL complète vers la page spécifique</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700">Type</label>
                  <select
                    id="type"
                    className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    value={formData.type || 'Commercial'}
                    onChange={handleInputChange}
                  >
                    <option value="Governmental">Gouvernemental</option>
                    <option value="Educational">Éducatif</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Non-profit">Organisation à but non lucratif</option>
                    <option value="Media">Média</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="reliability" className="block text-sm font-medium text-gray-700">Fiabilité</label>
                  <select
                    id="reliability"
                    className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    value={formData.reliability || 'Medium'}
                    onChange={handleInputChange}
                  >
                    <option value="High">Haute</option>
                    <option value="Medium">Moyenne</option>
                    <option value="Specialized">Spécialisée</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="relevanceScore" className="block text-sm font-medium text-gray-700">
                    Pertinence (1-10)
                  </label>
                  <input
                    type="number"
                    id="relevanceScore"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    value={formData.relevanceScore || 5}
                    onChange={(e) => {
                      const value = Math.min(10, Math.max(1, parseInt(e.target.value) || 5));
                      setFormData(prev => ({ ...prev, relevanceScore: value }));
                    }}
                    min="1"
                    max="10"
                  />
                </div>
                
                <div>
                  <label htmlFor="popularityScore" className="block text-sm font-medium text-gray-700">
                    Popularité (1-10)
                  </label>
                  <input
                    type="number"
                    id="popularityScore"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    value={formData.popularityScore || 5}
                    onChange={(e) => {
                      const value = Math.min(10, Math.max(1, parseInt(e.target.value) || 5));
                      setFormData(prev => ({ ...prev, popularityScore: value }));
                    }}
                    min="1"
                    max="10"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="px-6 py-4 bg-gray-50 text-right space-x-2">
            <button
              type="button"
              onClick={onEditClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!formData.domain}
              className={`px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white ${
                !formData.domain
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              Enregistrer
            </button>
          </div>
        </div>
      </Dialog>
      
      {/* Dialogue d'affichage du résumé complet */}
      <Dialog open={summaryDialogOpen} onClose={onSummaryClose} maxWidth="md" fullWidth>
        <div className="bg-white rounded-lg overflow-hidden shadow-xl max-w-4xl w-full">
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {source?.domain} - Résumé complet
            </h3>
            <button 
              onClick={onSummaryClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <Close className="h-5 w-5" />
            </button>
          </div>
          
          <div className="px-6 py-5">
            <div className="bg-purple-50 p-4 rounded-md border border-purple-100">
              <Typography variant="body1">{source?.summary}</Typography>
              
              {source?.summaryLanguage && (
                <div className="mt-3 flex items-center text-sm text-purple-700">
                  <InfoOutlined className="h-4 w-4 mr-2" />
                  Résumé généré par IA en {source.summaryLanguage === 'fr' ? 'français' : source.summaryLanguage}
                </div>
              )}
            </div>
                        
            <div className="mt-4 flex">
              
                href={source?.link || `https://${source?.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"

                <OpenInNew className="h-4 w-4 mr-2" />
                Visiter le site

            </div>
          </div>
          
          <div className="px-6 py-4 bg-gray-50 text-right">
            <button
              type="button"
              onClick={onSummaryClose}
              className="px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
            >
              Fermer
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}